import { useState, useEffect } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO, { generateBreadcrumbSchema } from '@/components/SEO';
import { getClients } from '@/lib/api';
import { Link } from 'react-router-dom';

const defaultClients = [
  { id: '1', name: 'Tech Innovators', logo: 'https://via.placeholder.com/200x80/121212/888888?text=TechInnovators' },
  { id: '2', name: 'Growth Labs', logo: 'https://via.placeholder.com/200x80/121212/888888?text=GrowthLabs' },
  { id: '3', name: 'Scale Ventures', logo: 'https://via.placeholder.com/200x80/121212/888888?text=ScaleVentures' },
  { id: '4', name: 'Revenue Pro', logo: 'https://via.placeholder.com/200x80/121212/888888?text=RevenuePro' },
  { id: '5', name: 'Digital First', logo: 'https://via.placeholder.com/200x80/121212/888888?text=DigitalFirst' },
  { id: '6', name: 'Market Leaders', logo: 'https://via.placeholder.com/200x80/121212/888888?text=MarketLeaders' },
];

export default function Clients() {
  const [clients, setClients] = useState(defaultClients);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const res = await getClients();
      if (res.data.length > 0) {
        setClients(res.data);
      }
    } catch (err) {
      console.log('Using default clients');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="clients-page">
      <SEO 
        title="Our Clients - Trusted by Growth Leaders"
        description="See the businesses that trust Wizbang for their digital marketing and growth needs. We've helped companies across India scale their revenue."
        keywords="wizbang clients, digital marketing clients, growth marketing portfolio, marketing agency portfolio India"
        url="/clients"
        structuredData={generateBreadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Clients", path: "/clients" }
        ])}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 bg-gradient-radial opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <p className="text-[#ff5a1f] font-mono text-sm uppercase tracking-widest mb-6">
            // Our Clients
          </p>
          <h1 className="font-syne text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Trusted by
            <br />
            <span className="text-[#ff5a1f]">Growth Leaders</span>
          </h1>
          <p className="text-[#888] text-lg md:text-xl max-w-2xl">
            We partner with ambitious brands that are serious about scaling revenue through systematic growth.
          </p>
        </div>
      </section>

      {/* Clients Grid */}
      <section className="py-24" data-testid="clients-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {clients.map((client) => (
              <div
                key={client.id}
                className="aspect-[2/1] bg-[#0A0A0A] border border-[#1F1F1F] hover:border-[#ff5a1f]/50 flex items-center justify-center p-8 grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-500"
                data-testid={`client-logo-${client.id}`}
              >
                {client.logo ? (
                  <img
                    src={client.logo}
                    alt={client.name}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span className="text-2xl font-bold text-[#555] font-syne">{client.name}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="clients-stats">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="font-syne text-5xl font-bold text-[#ff5a1f] mb-2">150+</p>
              <p className="text-[#888] uppercase tracking-wider text-sm">Clients Served</p>
            </div>
            <div>
              <p className="font-syne text-5xl font-bold text-[#ff5a1f] mb-2">20+</p>
              <p className="text-[#888] uppercase tracking-wider text-sm">Industries</p>
            </div>
            <div>
              <p className="font-syne text-5xl font-bold text-[#ff5a1f] mb-2">95%</p>
              <p className="text-[#888] uppercase tracking-wider text-sm">Client Retention</p>
            </div>
            <div>
              <p className="font-syne text-5xl font-bold text-[#ff5a1f] mb-2">5x</p>
              <p className="text-[#888] uppercase tracking-wider text-sm">Avg Growth Rate</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-radial opacity-30" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="font-syne text-3xl md:text-4xl font-bold text-white mb-6">
            Join Our Growing
            <br />
            <span className="text-[#ff5a1f]">Client Portfolio</span>
          </h2>
          <p className="text-[#888] text-lg mb-10">
            Ready to become our next success story?
          </p>
          <Link
            to="/book-appointment"
            className="bg-[#ff5a1f] text-white px-10 py-5 text-sm font-semibold uppercase tracking-wider hover:bg-[#e04e1a] transition-colors duration-300 btn-press"
          >
            Book Your Growth Call
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
